/**
 * MediaPickerModal — reusable modal for choosing an image from the Media Library.
 *
 * Used inside ImageBlock, GalleryBlock, HeroSection, SectionBlocks, and
 * any other component that needs to let the user pick an image.
 *
 * Features:
 *  - Shows project media (if inside template context) + global library toggle
 *  - Grid of thumbnails with search
 *  - Click to select, calls onSelect(signedUrl, mediaItem)
 *  - Upload directly from the picker
 *  - Responsive
 */

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { toast } from "sonner";
import {
  Search,
  X,
  Loader2,
  ImagePlus,
  Globe,
  Layers,
  Check,
  FolderOpen,
  Upload,
  Image as ImageIcon,
} from "lucide-react";
import { useTemplateSafe } from "./TemplateContext";
import { useContentEditingSafe } from "./ContentEditingContext";
import {
  listMedia,
  uploadMedia,
  fileToBase64,
  formatFileSize,
  type MediaItem,
} from "../../utils/mediaApi";

const btnReset =
  "appearance-none border-none font-inherit cursor-pointer p-0 m-0 leading-normal";

const ACCEPTED_TYPES = "image/jpeg,image/png,image/gif,image/webp,image/svg+xml,image/avif";

interface MediaPickerModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (url: string, item: MediaItem) => void;
  /** If provided, forces a specific templateId. Otherwise reads from TemplateContext. */
  templateId?: string;
}

type PickerTab = "project" | "global";

export function MediaPickerModal({ open, onClose, onSelect, templateId: forcedTemplateId }: MediaPickerModalProps) {
  const tplCtx = useTemplateSafe();
  const templateId = forcedTemplateId || tplCtx?.templateId || "";
  const hasProject = Boolean(templateId);

  const [tab, setTab] = useState<PickerTab>(hasProject ? "project" : "global");
  const [items, setItems] = useState<MediaItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 30;
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  // Search debounce
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset on tab change
  useEffect(() => {
    setPage(0);
  }, [tab, searchDebounced]);

  // Fetch
  const fetchItems = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const tplId = tab === "project" && hasProject ? templateId : undefined;
      const data = await listMedia({
        templateId: tplId,
        search: searchDebounced || undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error("[MediaPicker] fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [open, tab, hasProject, templateId, searchDebounced, page]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Reset state when opening
  useEffect(() => {
    if (open) {
      setTab(hasProject ? "project" : "global");
      setSearch("");
      setSearchDebounced("");
      setPage(0);
    }
  }, [open, hasProject]);

  // Upload handler
  const handleUploadFiles = useCallback(async (files: File[]) => {
    const imageFiles = files.filter((f) => f.type.startsWith("image/"));
    if (imageFiles.length === 0) return;
    if (imageFiles.some((f) => f.size > 10 * 1024 * 1024)) {
      toast.error("\u05D2\u05D5\u05D3\u05DC \u05DE\u05E7\u05E1\u05D9\u05DE\u05DC\u05D9 \u05DC\u05E7\u05D5\u05D1\u05E5: 10MB");
      return;
    }
    setUploading(true);
    const uploadTplId = tab === "project" && hasProject ? templateId : undefined;
    let lastUploaded: MediaItem | null = null;
    for (const file of imageFiles) {
      try {
        const base64 = await fileToBase64(file);
        lastUploaded = await uploadMedia({
          base64,
          originalName: file.name,
          folder: "general",
          templateId: uploadTplId,
        });
      } catch (err) {
        console.error("[MediaPicker] upload error:", err);
        toast.error(`\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D4\u05E2\u05DC\u05D0\u05EA ${file.name}`);
      }
    }
    setUploading(false);
    if (lastUploaded && imageFiles.length === 1) {
      // Auto-select single upload
      onSelect(lastUploaded.signedUrl, lastUploaded);
      onClose();
      toast.success("\u05EA\u05DE\u05D5\u05E0\u05D4 \u05D4\u05D5\u05E2\u05DC\u05EA\u05D4 \u05D5\u05E0\u05D1\u05D7\u05E8\u05D4");
    } else {
      fetchItems();
      if (imageFiles.length > 1) {
        toast.success(`${imageFiles.length} \u05EA\u05DE\u05D5\u05E0\u05D5\u05EA \u05D4\u05D5\u05E2\u05DC\u05D5`);
      }
    }
  }, [tab, hasProject, templateId, onSelect, onClose, fetchItems]);

  // Drag & Drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) handleUploadFiles(files);
  }, [handleUploadFiles]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-blue-600" />
            <h2 className="text-base font-bold text-gray-800">{"\u05D1\u05D7\u05D9\u05E8\u05EA \u05EA\u05DE\u05D5\u05E0\u05D4"}</h2>
          </div>
          <button type="button" className={`${btnReset} p-1 text-gray-400 hover:text-gray-600`} onClick={onClose}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs + Search */}
        <div className="px-4 pt-2 space-y-2">
          {/* Tabs */}
          {hasProject && (
            <div className="flex items-center gap-1 border-b border-gray-100 pb-0">
              <button
                type="button"
                className={`${btnReset} px-3 py-1.5 text-sm font-medium border-b-2 ${
                  tab === "project"
                    ? "border-emerald-500 text-emerald-700"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => setTab("project")}
              >
                <span className="inline-flex items-center gap-1">
                  <Layers className="w-3.5 h-3.5" />
                  {"\u05DE\u05D3\u05D9\u05D4 \u05E4\u05E8\u05D5\u05D9\u05E7\u05D8"}
                </span>
              </button>
              <button
                type="button"
                className={`${btnReset} px-3 py-1.5 text-sm font-medium border-b-2 ${
                  tab === "global"
                    ? "border-blue-500 text-blue-700"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => setTab("global")}
              >
                <span className="inline-flex items-center gap-1">
                  <Globe className="w-3.5 h-3.5" />
                  {"\u05E1\u05E4\u05E8\u05D9\u05D9\u05D4 \u05DB\u05DC\u05DC\u05D9\u05EA"}
                </span>
              </button>
            </div>
          )}

          {/* Search + Upload */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={"\u05D7\u05D9\u05E4\u05D5\u05E9 \u05EA\u05DE\u05D5\u05E0\u05D4..."}
                className="w-full pr-9 pl-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-gray-50"
              />
              {search && (
                <button type="button" className={`${btnReset} absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600`} onClick={() => setSearch("")}>
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <button
              type="button"
              className={`${btnReset} inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700`}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
              {"\u05D4\u05E2\u05DC\u05D0\u05D4"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_TYPES}
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length > 0) handleUploadFiles(files);
                e.target.value = "";
              }}
            />
          </div>
        </div>

        {/* Drag overlay */}
        {dragOver && (
          <div className="mx-4 mt-2 p-6 border-2 border-dashed border-blue-400 rounded-xl bg-blue-50 flex items-center justify-center">
            <div className="text-center">
              <Upload className="w-8 h-8 mx-auto text-blue-500 mb-1" />
              <p className="text-sm font-medium text-blue-700">{"\u05E9\u05D7\u05E8\u05E8 \u05EA\u05DE\u05D5\u05E0\u05D4 \u05DB\u05D0\u05DF"}</p>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-3 min-h-0">
          {loading && items.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16">
              <ImageIcon className="w-12 h-12 mx-auto text-gray-200 mb-3" />
              <p className="text-gray-500 text-sm mb-1">
                {searchDebounced
                  ? "\u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0\u05D5 \u05EA\u05DE\u05D5\u05E0\u05D5\u05EA \u05EA\u05D5\u05D0\u05DE\u05D5\u05EA"
                  : tab === "project"
                    ? "\u05D0\u05D9\u05DF \u05E2\u05D3\u05D9\u05D9\u05DF \u05EA\u05DE\u05D5\u05E0\u05D5\u05EA \u05D1\u05E4\u05E8\u05D5\u05D9\u05E7\u05D8"
                    : "\u05D4\u05E1\u05E4\u05E8\u05D9\u05D9\u05D4 \u05E8\u05D9\u05E7\u05D4"}
              </p>
              <p className="text-gray-400 text-xs">{"\u05D4\u05E2\u05DC\u05D4 \u05EA\u05DE\u05D5\u05E0\u05D4 \u05D0\u05D5 \u05D2\u05E8\u05D5\u05E8 \u05DC\u05DB\u05D0\u05DF"}</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
              {items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`${btnReset} group relative rounded-lg border border-gray-200 overflow-hidden hover:border-blue-400 hover:shadow-md focus:outline-none focus:border-blue-500`}
                  onClick={() => {
                    onSelect(item.signedUrl, item);
                    onClose();
                  }}
                >
                  <div className="aspect-square bg-gray-100 overflow-hidden">
                    <img
                      src={item.signedUrl}
                      alt={item.alt || item.originalName}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    />
                  </div>
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-blue-600/0 group-hover:bg-blue-600/20 flex items-center justify-center opacity-0 group-hover:opacity-100" style={{ transition: "opacity 0.15s" }}>
                    <div className="w-8 h-8 rounded-full bg-white shadow flex items-center justify-center">
                      <Check className="w-4 h-4 text-blue-600" />
                    </div>
                  </div>
                  {/* Name */}
                  <div className="px-1.5 py-1 bg-white">
                    <p className="text-[10px] text-gray-600 truncate" title={item.originalName}>{item.originalName}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4 pb-1">
              <button
                type="button"
                className={`${btnReset} px-2.5 py-1 rounded text-xs border border-gray-200 ${page > 0 ? "hover:bg-gray-100 text-gray-700" : "text-gray-300 cursor-default"}`}
                onClick={() => page > 0 && setPage(page - 1)}
                disabled={page === 0}
              >
                {"\u05D4\u05E7\u05D5\u05D3\u05DD"}
              </button>
              <span className="text-xs text-gray-500">{page + 1} / {totalPages}</span>
              <button
                type="button"
                className={`${btnReset} px-2.5 py-1 rounded text-xs border border-gray-200 ${page < totalPages - 1 ? "hover:bg-gray-100 text-gray-700" : "text-gray-300 cursor-default"}`}
                onClick={() => page < totalPages - 1 && setPage(page + 1)}
                disabled={page >= totalPages - 1}
              >
                {"\u05D4\u05D1\u05D0"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * EditableImage — a reusable image component that integrates with the media picker.
 *
 * In view mode: shows the image (or placeholder).
 * In edit mode: click to open MediaPickerModal, hover shows change button.
 *
 * Usage:
 *   <EditableImage
 *     src={meta.imageUrl}
 *     onChangeSrc={(url) => onUpdateMeta({ imageUrl: url })}
 *     isEditMode={isEditMode}
 *     alt="description"
 *     className="w-full h-64 object-cover rounded-xl"
 *     placeholderClassName="h-64"
 *   />
 */

interface EditableImageProps {
  src?: string;
  onChangeSrc: (url: string, item?: MediaItem) => void;
  isEditMode: boolean;
  alt?: string;
  className?: string;
  placeholderClassName?: string;
  placeholderText?: string;
  /** Override templateId for media picker */
  templateId?: string;
}

export function EditableImage({
  src,
  onChangeSrc,
  isEditMode,
  alt = "",
  className = "w-full h-64 object-cover",
  placeholderClassName = "h-64",
  placeholderText,
  templateId,
}: EditableImageProps) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleEditClick = useCallback((e: React.MouseEvent) => {
    if (!isEditMode) return;
    e.preventDefault();
    e.stopPropagation();
    setPickerOpen(true);
  }, [isEditMode]);

  if (!src) {
    // Placeholder
    return (
      <>
        <div
          className={`w-full bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-dashed rounded-xl flex items-center justify-center ${placeholderClassName} ${isEditMode ? "border-blue-300 cursor-pointer hover:border-blue-500 hover:from-blue-100 hover:to-indigo-100" : "border-gray-200"}`}
          onClick={handleEditClick}
        >
          <div className="text-center">
            <ImagePlus className={`w-10 h-10 mx-auto mb-2 ${isEditMode ? "text-blue-400" : "text-gray-300"}`} />
            {isEditMode ? (
              <p className="text-xs font-semibold text-blue-600">{"\u05DC\u05D7\u05E6\u05D5 \u05DC\u05D1\u05D7\u05D9\u05E8\u05EA \u05EA\u05DE\u05D5\u05E0\u05D4"}</p>
            ) : (
              <p className="text-[11px] text-gray-400">{placeholderText || "\u05EA\u05DE\u05D5\u05E0\u05D4"}</p>
            )}
          </div>
        </div>
        {pickerOpen && (
          <MediaPickerModal
            open={pickerOpen}
            onClose={() => setPickerOpen(false)}
            onSelect={(url, item) => onChangeSrc(url, item)}
            templateId={templateId}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div
        className={`relative group/editable-img ${isEditMode ? "cursor-pointer" : ""}`}
        onClick={handleEditClick}
      >
        <img
          src={src}
          alt={alt}
          className={`${className} ${isEditMode ? "group-hover/editable-img:opacity-80" : ""}`}
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = "none";
          }}
        />
        {isEditMode && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/editable-img:opacity-100" style={{ transition: "opacity 0.15s" }}>
            <div className="bg-white/90 rounded-lg px-3 py-2 shadow-lg flex items-center gap-2 text-sm font-medium text-blue-700">
              <ImagePlus className="w-4 h-4" />
              {"\u05D4\u05D7\u05DC\u05E3 \u05EA\u05DE\u05D5\u05E0\u05D4"}
            </div>
          </div>
        )}
      </div>
      {pickerOpen && (
        <MediaPickerModal
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onSelect={(url, item) => onChangeSrc(url, item)}
          templateId={templateId}
        />
      )}
    </>
  );
}

/**
 * ContentEditableImage -- like EditableText but for images.
 * Reads/writes imageUrl from ContentEditingContext by contentKey.
 * Drop into any render function (no hooks needed -- it is its own component).
 */

interface ContentEditableImageProps {
  contentKey: string;
  defaultSrc?: string;
  alt?: string;
  className?: string;
  placeholderClassName?: string;
  placeholderText?: string;
}

export function ContentEditableImage({
  contentKey,
  defaultSrc = "",
  alt = "",
  className,
  placeholderClassName,
  placeholderText,
}: ContentEditableImageProps) {
  const ctx = useContentEditingSafe();
  const isEditMode = ctx?.isEditMode ?? false;
  const src = ctx?.getContent?.(contentKey, defaultSrc) ?? defaultSrc;

  return (
    <EditableImage
      src={src}
      onChangeSrc={(url) => ctx?.saveContent?.(contentKey, url)}
      isEditMode={isEditMode}
      alt={alt}
      className={className}
      placeholderClassName={placeholderClassName}
      placeholderText={placeholderText}
    />
  );
}